// Cartus message-only playback. No consumer TTS endpoint or persistent storage.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_AUDIO_BYTES = 8 * 1024 * 1024;

export function attachMessageSpeech(form, { page = document, windowTarget = window,
  fetcher = fetch, player, urls = URL,
  speed = () => Number(form?.dataset.speedMultiplier) || 1,
  audioError = () => I18n.t('javascript.errors.audio_unavailable'),
  onReplyEnded = () => {},
  mode = () => form?.dataset.mode,
  csrf = page.querySelector('meta[name="csrf-token"]')?.content } = {}) {
  if (form?.dataset.serverMediatedAi !== 'true' || form.dataset.messageSpeechEnabled !== 'true') return () => {};
  form.__tenantMessageSpeech?.();
  const chatId = form.dataset.contentId;
  let controller, audio, objectUrl, activeButton, timer, generation = 0, destroyed = false, synchronized = false;
  const automaticPlayback = () => ['audio_only', 'text_audio'].includes(mode());
  // Remember every delivered ID, including deliveries received in text-only mode.
  // Never synthesize old messages on reload or replay a duplicate completion.
  const delivered = new Set([...page.querySelectorAll('.js-cartus-message-speech')].map(button => button.dataset.chatMessageId));
  const fail = () => player.showAudioError(audioError());

  function stop() {
    generation += 1;
    controller?.abort(); controller = null;
    clearTimeout(timer); timer = null;
    if (synchronized) {
      windowTarget.cancelAnimationFrame(player.animationFrameId);
      player.reset(); synchronized = false;
    }
    if (audio) { audio.stop(); audio.unload();
      player.audioPool = player.audioPool.filter(item => item !== audio);
      if (player.player === audio) { player.player = null; player.soundPlaying = false; }
      audio = null; }
    if (objectUrl) { urls.revokeObjectURL(objectUrl); objectUrl = null; }
    if (activeButton) {
      activeButton.classList.remove('pulse');
      activeButton.setAttribute('aria-pressed', 'false');
      activeButton.removeAttribute('aria-busy');
      activeButton = null;
    }
  }

  async function click(event) {
    const button = event.target.closest?.('.js-cartus-message-speech');
    if (!button) return;
    event.preventDefault();
    if (button === activeButton) {
      if (audio && player.player === audio) { player.togglePlayer(); button.setAttribute('aria-pressed', String(audio.playing())); }
      else stop();
      return;
    }
    return play(button);
  }

  async function play(button, reply = false) {
    stop();
    const messageId = button.dataset.chatMessageId;
    const suggestion = button.dataset.suggestionIndex;
    const selection = button.dataset.speechSelection;
    if (selection !== undefined && (selection !== 'alternative' || suggestion !== undefined)) return;
    if (suggestion !== undefined && !['0', '1'].includes(suggestion)) return;
    if (!UUID.test(chatId || '') || !UUID.test(messageId || '') || !csrf) {
      fail(); return;
    }
    const current = generation;
    activeButton = button;
    button.classList.add('pulse'); button.setAttribute('aria-busy', 'true');
    controller = new AbortController();
    const signal = controller.signal;
    timer = setTimeout(() => controller?.abort(), 30000);
    try {
      const response = await fetcher(`/langua/cartus_chats/${chatId}/messages/${messageId}/audio`, {
        method: 'POST', credentials: 'same-origin', redirect: 'error', cache: 'no-store', signal,
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
        body: JSON.stringify(selection ? { selection } : suggestion === undefined ? {} : { suggestion_index: Number(suggestion) })
      });
      const type = response.headers.get('content-type')?.split(';')[0];
      if (!response.ok || !['audio/mpeg', 'application/json'].includes(type)) throw new Error('audio_unavailable');
      const limit = type === 'application/json' ? 16 * 1024 * 1024 : MAX_AUDIO_BYTES;
      const declared = response.headers.get('content-length');
      if (declared && (!/^\d+$/.test(declared) || Number(declared) > limit)) throw new Error('audio_unavailable');
      const reader = response.body.getReader();
      const chunks = []; let size = 0;
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          size += value.byteLength;
          if (size > limit || signal.aborted || generation !== current) throw new Error('audio_unavailable');
          chunks.push(value);
        }
      } finally { await reader.cancel(); }
      if (size < 16 || (declared && size !== Number(declared)) || signal.aborted || generation !== current) throw new Error('audio_unavailable');
      clearTimeout(timer); timer = null;
      let alignment = null, audioChunks = chunks;
      if (type === 'application/json') {
        const body = new Uint8Array(size); let offset = 0;
        for (const chunk of chunks) { body.set(chunk, offset); offset += chunk.byteLength; }
        const decoded = decodeTimedAudio(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(body)));
        audioChunks = [decoded.bytes]; alignment = decoded.alignment;
      }
      objectUrl = urls.createObjectURL(new Blob(audioChunks, { type: 'audio/mpeg' }));
      const rate = button.dataset.playbackRate === '0.75' ? 0.75 : speed();
      player.setupPlayer(objectUrl, button, rate);
      audio = player.player;
      if (alignment && !['japanese', 'chinese'].includes(form.dataset.language)) {
        const wordRoot = selection === 'alternative' ? button.closest?.('.chat-alternative-response') :
          suggestion !== undefined ? (button.closest?.('.word-container') || page.getElementById?.('guided-mode-suggestion')) :
          button.closest?.('.chat-message')?.querySelector('.chat-message-content');
        if (wordRoot) {
          const timings = timingsForRenderedWords(alignment, wordRoot.querySelectorAll('.js-audio-word.word'));
          if (timings) {
            player.wordTimings = timings; player.wordElement = wordRoot; player.ttsContext = null;
            // Howler seek() is media time even at a non-1 playback rate.
            player.setupWordElements(timings, wordRoot, 1);
            player.setupSync(); synchronized = true;
          }
        }
      }
      audio.once('end', () => { if (current === generation) { stop(); if (reply) onReplyEnded(); } });
      audio.once('loaderror', () => { if (current === generation) { stop(); fail(); } });
      audio.once('playerror', () => { if (current === generation) { stop(); fail(); } });
      button.classList.remove('pulse'); button.removeAttribute('aria-busy'); button.setAttribute('aria-pressed', 'true');
      audio.play();
    } catch (_) {
      if (current === generation) { stop(); fail(); }
    }
  }

  async function playReply(messageId) {
    if (destroyed || !UUID.test(messageId || '') || delivered.has(messageId)) return;
    delivered.add(messageId);
    if (!automaticPlayback()) return;
    // Match the consumer player: each new reply replaces previous playback.
    // play() cancels pending audio and releases the previous player first.
    const button = page.querySelector(`.js-cartus-message-speech[data-chat-message-id="${messageId}"]:not([data-playback-rate]):not([data-suggestion-index]):not([data-speech-selection])`);
    if (button) await play(button, true);
  }

  function interrupt(event) {
    if (event.type === 'submit' || event.target.closest?.('#js-record-query-btn')) { stop(); }
  }

  function destroy() {
    destroyed = true;
    stop();
    form.removeEventListener('submit', interrupt);
    page.removeEventListener('click', interrupt);
    page.removeEventListener('click', click);
    page.removeEventListener('turbolinks:before-cache', destroy);
    page.removeEventListener('turbo:before-cache', destroy);
    windowTarget.removeEventListener('pagehide', destroy);
    delete form.__tenantMessageSpeech;
  }
  form.addEventListener('submit', interrupt);
  page.addEventListener('click', interrupt);
  page.addEventListener('click', click);
  page.addEventListener('turbolinks:before-cache', destroy);
  page.addEventListener('turbo:before-cache', destroy);
  windowTarget.addEventListener('pagehide', destroy);
  destroy.playReply = playReply;
  form.__tenantMessageSpeech = destroy;
  return destroy;
}

// Only the documented original-text alignment leaves the owned Rails endpoint.
export function decodeTimedAudio(data) {
  if (!data || typeof data.audio_base64 !== 'string' || data.audio_base64.length > Math.ceil(MAX_AUDIO_BYTES / 3) * 4) throw new Error('audio_unavailable');
  const raw = atob(data.audio_base64), bytes = Uint8Array.from(raw, c => c.charCodeAt(0));
  if (bytes.length < 16 || bytes.length > MAX_AUDIO_BYTES || !(raw.startsWith('ID3') || (bytes[0] === 255 && (bytes[1] & 0xe0) === 0xe0))) throw new Error('audio_unavailable');
  const alignment = data.alignment;
  if (!alignment) throw new Error('audio_unavailable');
  const { characters: chars, character_start_times_seconds: starts, character_end_times_seconds: ends } = alignment;
  if (![chars, starts, ends].every(a => Array.isArray(a) && a.length > 0 && a.length <= 8000) || chars.length !== starts.length || chars.length !== ends.length) throw new Error('audio_unavailable');
  let previous = 0;
  chars.forEach((c, i) => {
    if (typeof c !== 'string' || !c.length || c.length > 64 || ![starts[i], ends[i]].every(t => Number.isFinite(t) && t >= 0 && t <= 1800) || starts[i] < previous || ends[i] < starts[i]) throw new Error('audio_unavailable');
    previous = starts[i];
  });
  return { bytes, alignment };
}

// Match the complete visible sequence, never search ahead or infer missing words.
// The consumer renderer may group bold words and gives punctuation its own span.
export function timingsForRenderedWords(alignment, elements) {
  const spans = Array.from(elements);
  if (!spans.length) return null;
  const source = alignment.characters.join('');
  const indices = alignment.characters.flatMap((part, index) => part.split('').map(() => index));
  const match = (omitted = new Set()) => {
    const chars = [], offsets = [];
    for (let i = 0; i < source.length; i++) {
      if (omitted.has(i)) continue;
      const char = /\s/u.test(source[i]) ? ' ' : source[i];
      if (char === ' ' && chars.at(-1) === ' ') continue;
      chars.push(char); offsets.push(indices[i]);
    }
    let cursor = 0;
    const result = { words: [], word_start_times_ms: [], word_durations_ms: [] };
    for (const span of spans) {
      const text = (span.textContent || '').replace(/\s+/gu, ' ').trim();
      if (!text) return null;
      while (chars[cursor] === ' ') cursor++;
      if (chars.slice(cursor, cursor + text.length).join('') !== text) return null;
      const first = offsets[cursor], last = offsets[cursor + text.length - 1];
      const start = alignment.character_start_times_seconds[first] * 1000;
      const end = alignment.character_end_times_seconds[last] * 1000;
      if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
      result.words.push(text); result.word_start_times_ms.push(start); result.word_durations_ms.push(end - start);
      cursor += text.length;
    }
    while (chars[cursor] === ' ') cursor++;
    return cursor === chars.length ? result : null;
  };
  const exact = match();
  if (exact) return exact;
  // Only strip paired emphasis delimiters when the existing DOM rendered bold.
  // All punctuation and text still have to match; unsupported markup skips sync.
  if (!spans.some(span => span.querySelector?.('b, strong'))) return null;
  const omitted = new Set();
  for (const marker of source.matchAll(/\*\*([^*\n]+)\*\*|\*([^*\n]+)\*/g)) {
    const width = marker[1] === undefined ? 1 : 2;
    for (let n = 0; n < width; n++) {
      omitted.add(marker.index + n); omitted.add(marker.index + marker[0].length - 1 - n);
    }
  }
  return omitted.size ? match(omitted) : null;
}
